import React from 'react';
import { Link } from 'react-router-dom';
import platformImage from '../assests/main image.png';

const buildCapabilities = [
  'Sending WhatsApp messages', 'Sending approved templates', 'Sending media',
  'Managing templates', 'Processing webhooks', 'Tracking message status',
  'Automating notifications', 'Running campaigns', 'Managing customers',
  'Integrating CRM workflows',
];
const developmentUseCases = [
  ['CRM Applications', 'Send customer notifications and connect WhatsApp activity with customer records.'],
  ['SaaS Platforms', 'Provide WhatsApp functionality to businesses using your software.'],
  ['E-commerce Applications', 'Send supported order, delivery, and customer notifications.'],
  ['Appointment Systems', 'Send booking confirmations and reminders.'],
  ['ERP Systems', 'Trigger business notifications from application events.'],
  ['Helpdesk Applications', 'Connect customer communication with support workflows.'],
  ['Marketing Platforms', 'Build campaign workflows around approved WhatsApp templates.'],
  ['Custom Business Software', 'Add WhatsApp communication to existing applications.'],
];
const restOperations = [
  ['Message sending', 'Send supported messages from an application.'],
  ['Template messaging', 'Use approved templates for supported communication.'],
  ['Media messaging', 'Build workflows using supported media types.'],
  ['Customer management', 'Connect communication with customer records.'],
  ['Campaign operations', 'Integrate campaign workflows into business applications.'],
  ['Template operations', 'Manage supported template workflows.'],
  ['Business communication', 'Connect API requests to application events.'],
];
const nodeTasks = [
  'Authenticate API requests', 'Send WhatsApp messages', 'Process API responses',
  'Store message information', 'Handle webhooks', 'Update customer records',
  'Trigger automated workflows', 'Schedule communication',
];
const phpTasks = [
  'Trigger WhatsApp notifications', 'Send template messages', 'Process webhook events',
  'Store message records', 'Connect WhatsApp with CRM data',
  'Create automated communication workflows',
];
const sensitiveConfig = [
  'API credentials', 'Access tokens', 'Business identifiers',
  'WhatsApp account configuration', 'Webhook verification', 'Environment variables',
  'Server-side API requests',
];
const templateTasks = [
  'Template selection', 'Template identifiers', 'Language selection',
  'Variable substitution', 'Template parameters', 'Approval status',
  'Message delivery tracking',
];
const mediaExamples = [
  ['E-commerce', 'Product documents or order information.'],
  ['Education', 'Course documents.'],
  ['Real Estate', 'Property information.'],
  ['Business', 'Invoices and reports.'],
  ['Support', 'Screenshots and documents.'],
];
const webhookEvents = ['Message status', 'Delivery updates', 'Read status', 'Incoming communication', 'Other supported business events'];
const messageStates = ['Sent', 'Delivered', 'Read', 'Failed'];
const apiErrors = [
  'Authentication errors', 'Invalid parameters', 'Invalid template configuration',
  'Missing required fields', 'Unsupported media', 'API failures', 'Rate limitations',
  'Webhook failures', 'Network errors',
];
const bestPractices = [
  ['Keep Credentials Server-Side', 'Never place private API credentials directly inside frontend code. Keep sensitive configuration on your backend.'],
  ['Validate Input', 'Validate phone numbers, template parameters, and message data before sending API requests.'],
  ['Use Environment Variables', 'Store secrets and configuration outside your source code.'],
  ['Log API Responses', 'Maintain useful logs for debugging and production monitoring without exposing sensitive credentials.'],
  ['Process Webhooks Reliably', 'Acknowledge webhook requests correctly and process events safely.'],
  ['Make Webhook Processing Idempotent', 'Avoid processing the same event multiple times when duplicate deliveries occur.'],
  ['Handle Failures', 'Build appropriate error handling and retry mechanisms.'],
  ['Separate Environments', 'Use separate configurations and environments for testing and production.'],
];
const testingSteps = [
  ['Test API Authentication', 'Verify credentials and account configuration.'],
  ['Test Message Requests', 'Test supported message types and API responses.'],
  ['Test Templates', 'Verify template identifiers, languages, and variables.'],
  ['Test Webhooks', 'Confirm that your application receives and processes events correctly.'],
  ['Test Error Conditions', 'Test invalid requests and failure responses.'],
  ['Test Production Workflow', 'Verify the complete application flow before enabling production communication.'],
];
const documentationTopics = ['Authentication', 'Base URL', 'Endpoints', 'Request parameters', 'Response format', 'Error responses', 'Webhooks', 'Templates', 'Media', 'Message status', 'Examples', 'Production requirements'];
const startupUseCases = ['Customer support', 'Notifications', 'Appointment systems', 'E-commerce communication', 'Lead management', 'CRM', 'Marketing automation', 'Business alerts'];
const architectureConsiderations = [
  'Application and backend separation', 'Business logic', 'WhatsApp service',
  'HAWKTRION API integration', 'WhatsApp platform connectivity', 'Customer communication',
];
const developerBenefits = [
  ['API-First Integration', 'Connect your existing application through APIs instead of rebuilding it.'],
  ['Developer-Focused', 'Build WhatsApp functionality into your own backend and workflows.'],
  ['Multi-Tenant Ready', 'Support SaaS applications serving multiple businesses.'],
  ['Template Support', 'Integrate approved template messaging into application workflows.'],
  ['Media Messaging', 'Build supported image, video, document, and audio workflows.'],
  ['Webhooks', 'Process supported WhatsApp events in your backend.'],
  ['Campaign Support', 'Build campaign and broadcast functionality into business applications.'],
  ['CRM Integration', 'Connect WhatsApp communication with customers and leads.'],
  ['Automation', 'Trigger communication from application events.'],
];
const developerRequirements = [
  ['REST API', 'Connect the application backend'],
  ['Messaging', 'Send WhatsApp communication'],
  ['Templates', 'Build approved template workflows'],
  ['Media', 'Send supported media'],
  ['Webhooks', 'Process events'],
  ['Status tracking', 'Monitor message lifecycle'],
  ['CRM', 'Connect customer communication'],
  ['Automation', 'Trigger messages from events'],
  ['Campaigns', 'Build business messaging workflows'],
  ['SaaS', 'Provide WhatsApp features to tenants'],
];
const requestSteps = [
  ['Your Application Creates an Event', 'For example, an order is confirmed.'],
  ['Backend Processes the Event', 'Your server determines which communication should be sent.'],
  ['HAWKTRION API Is Called', 'Your backend sends the required API request.'],
  ['WhatsApp Processes the Message', 'The supported WhatsApp messaging infrastructure processes the request.'],
  ['Customer Receives the Message', 'The message reaches the customer when successfully processed and delivered.'],
  ['Status Events Are Processed', 'Your application can process available delivery, read, and status events through webhooks.'],
];
const developerChecklist = [
  'API credentials are stored securely', 'Authentication is implemented',
  'API endpoints are configured', 'Request validation is implemented',
  'Template parameters are validated', 'Media handling is tested',
  'Webhook endpoint is configured', 'Webhook events are processed',
  'Duplicate events are handled', 'Message status is stored',
  'API errors are logged', 'Retry logic is appropriate',
  'Development and production environments are separated',
  'WhatsApp and Meta requirements are followed',
];
const faqs = [
  ['What is WhatsApp API for developers?', 'WhatsApp API for developers provides a programmatic way to integrate WhatsApp communication into websites, SaaS applications, CRMs, and other software.'],
  ['Can I integrate WhatsApp API with Node.js?', 'Yes. A Node.js or Express.js backend can communicate with HAWKTRION through HTTP APIs.'],
  ['Can I use WhatsApp API with PHP?', 'Yes. PHP applications can integrate WhatsApp functionality using server-side HTTP API requests.'],
  ['Can I integrate WhatsApp API with Laravel?', 'Yes. Laravel applications can use backend API requests to integrate WhatsApp communication into application workflows.'],
  ['Does HAWKTRION provide REST APIs?', 'HAWKTRION provides API-based functionality for integrating WhatsApp communication into applications.'],
  ['Can developers send WhatsApp templates through an API?', 'Yes. Developers can build template-based messaging workflows using supported and approved WhatsApp templates.'],
  ['Can I receive WhatsApp webhook events?', 'Webhook functionality can be used to process supported WhatsApp events and synchronize them with your application.'],
  ['Can I track WhatsApp message status?', 'Your application can process available status events and maintain message lifecycle information.'],
  ['Can I send images and documents through WhatsApp API?', 'Supported media types can be integrated depending on WhatsApp API capabilities and applicable requirements.'],
  ['Can I build a WhatsApp chatbot?', 'Yes. Developers can combine APIs, webhooks, business logic, and automation to build WhatsApp conversational workflows.'],
  ['Can I integrate WhatsApp with my CRM?', 'Yes. WhatsApp communication can be connected with CRM customers, leads, appointments, and other application records.'],
  ['Can I use HAWKTRION for a SaaS application?', 'Yes. HAWKTRION can be used as a WhatsApp communication layer for SaaS platforms and multi-tenant applications.'],
  ['Is HAWKTRION an official WhatsApp or Meta product?', 'No. HAWKTRION is an independent software platform. WhatsApp and Meta are separate products and companies, and integrations remain subject to applicable WhatsApp and Meta requirements and policies.'],
];


const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="business-api-heading">
    {eyebrow && <div className="sec-label"><span className="sec-label-dot"></span>{eyebrow}</div>}
    <h2>{title}</h2>
    {children && <p>{children}</p>}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="business-api-list">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const CardGrid = ({ items }) => (
  <div className="business-api-grid">
    {items.map(([title, description]) => (
      <article className="business-api-card" key={title}>
        <h3>{title}</h3>
        <p>{description}</p>
      </article>
    ))}
  </div>
);

const Workflow = ({ steps }) => (
  <ol className="business-api-workflow">
    {steps.map((step, index) => (
      <li key={step}><span className="business-api-step-number">{index + 1}</span><span>{step}</span></li>
    ))}
  </ol>
);

const StepList = ({ items }) => (
  <ol className="business-api-steps">
    {items.map(([title, description], index) => (
      <li className="business-api-step" key={title}>
        <span className="business-api-step-number">{index + 1}</span>
        <div><h3>{title}</h3><p>{description}</p></div>
      </li>
    ))}
  </ol>
);

const Checklist = ({ items }) => (
  <ul className="developer-api-checklist">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const CodeFlow = ({ lines }) => (
  <pre className="developer-api-code"><code>{lines.join('\n')}</code></pre>
);

const WhatsappApiForDevelopersContent = () => (
  <div className="business-api-content developer-api-content">
    <section className="business-api-section business-api-intro">
      <div className="container">
        <div className="business-api-intro-grid">
          <div className="business-api-intro-copy">
            <p className="business-api-lead">Build powerful WhatsApp integrations with APIs designed for developers.</p>
            <p>Connect your application to WhatsApp messaging with HAWKTRION. Integrate REST APIs, message templates, media, webhooks, automation, campaigns, and customer communication into web or SaaS applications.</p>
            <p className="developer-api-emphasis">Build your WhatsApp integration with a developer-friendly API platform.</p>
            <div className="business-api-actions">
              <Link to="/signup" className="btn btn-p">Get Started</Link>
              <Link to="/whatsapp-api-documentation/" className="btn btn-s">View API Documentation</Link>
            </div>
          </div>
          <figure className="business-api-visual">
            <img src={platformImage} alt="HAWKTRION WhatsApp platform overview with APIs, webhooks, messaging, CRM, and automation" />
            <figcaption>Integrate messaging, templates, webhooks, and automation into your application.</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Developer APIs" title="Build WhatsApp Integrations With Developer-Friendly APIs">
          Add WhatsApp communication directly to your application. HAWKTRION provides an API-based platform for integrating messaging into websites, SaaS applications, CRMs, business software, ecommerce platforms, and custom applications.
        </SectionHeading>
        <BulletList items={buildCapabilities} />
        <p className="business-api-copy developer-api-emphasis">Build your application. Let HAWKTRION handle the WhatsApp communication layer.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Application Architecture" title="WhatsApp API Development for Modern Applications">
          Integrate WhatsApp directly into your application backend instead of creating a separate communication workflow.
        </SectionHeading>
        <CodeFlow lines={['Your Application', '      ↓', 'Your Backend', '      ↓', 'HAWKTRION API', '      ↓', 'WhatsApp Business / Cloud API', '      ↓', 'Customer']} />
        <p className="business-api-copy">Your application controls business logic while HAWKTRION provides WhatsApp communication functionality.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Development Use Cases" title="What Can Developers Build With WhatsApp API?" />
        <CardGrid items={developmentUseCases} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="REST API" title="REST API for WhatsApp Integration">
          REST APIs make it possible to integrate WhatsApp communication into applications built with different programming languages and frameworks.
        </SectionHeading>
        <p className="business-api-copy">Your backend can send API requests for supported operations such as:</p>
        <CardGrid items={restOperations} />
        <p className="business-api-copy">Use your preferred backend technology while connecting to the HAWKTRION API.</p>
        <Link to="/whatsapp-api-documentation/" className="business-api-text-link">Explore HAWKTRION API Documentation</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Node.js" title="WhatsApp API With Node.js">
          Integrate with Node.js applications through HTTP API requests. This approach works well for SaaS platforms and custom business applications.
        </SectionHeading>
        <BulletList items={nodeTasks} />
        <h3 className="business-api-subheading">Example Architecture</h3>
        <CodeFlow lines={['React / Next.js Frontend', '          ↓', 'Node.js / Express Backend', '          ↓', 'HAWKTRION API', '          ↓', 'WhatsApp Business API']} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="PHP & Laravel" title="WhatsApp API With PHP and Laravel">
          PHP developers can integrate WhatsApp functionality into existing PHP applications and Laravel projects.
        </SectionHeading>
        <BulletList items={phpTasks} />
        <p className="business-api-copy">WhatsApp can become another communication channel inside your existing Laravel application.</p>
      </div>
    </section>

    <section className="business-api-section developer-api-security">
      <div className="container">
        <SectionHeading eyebrow="Security" title="WhatsApp API Authentication">
          Secure API authentication is an important part of any production integration. Your application should securely manage:
        </SectionHeading>
        <BulletList items={sensitiveConfig} />
        <p className="developer-api-warning">Never expose private API credentials in frontend JavaScript or public source code. Keep sensitive configuration on your backend and use secure server-to-server communication.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Messaging" title="Send WhatsApp Messages Through APIs">
          Build application workflows that send supported WhatsApp messages through APIs.
        </SectionHeading>
        <Workflow steps={['Customer places order', 'Application processes order', 'Backend triggers WhatsApp API', 'WhatsApp processes message', 'Customer receives notification']} />
        <p className="business-api-copy">WhatsApp communication becomes part of your application&apos;s business logic.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Template Workflows" title="WhatsApp Template API">
          Business-initiated WhatsApp communication may require approved templates, depending on conversation context and applicable requirements. Select an appropriate approved template for each business event.
        </SectionHeading>
        <BulletList items={templateTasks} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Media" title="WhatsApp Media API">
          Modern applications often need to send more than text. Integrate supported media messaging into your product.
        </SectionHeading>
        <BulletList items={['Images', 'Videos', 'Documents', 'Audio']} />
        <CardGrid items={mediaExamples} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Event Processing" title="WhatsApp Webhooks for Developers">
          Webhooks allow your application to receive events from the WhatsApp communication layer. Your backend can process events and update application data.
        </SectionHeading>
        <CodeFlow lines={['WhatsApp Event', '      ↓', 'Webhook Endpoint', '      ↓', 'Your Backend', '      ↓', 'Database', '      ↓', 'Application UI']} />
        <h3 className="business-api-subheading">Supported Event Workflows</h3>
        <BulletList items={webhookEvents} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Observability" title="Track WhatsApp Message Status">
          Your application can maintain communication states, store available events, and display the latest status to users.
        </SectionHeading>
        <div className="developer-api-status-row">
          {messageStates.map((state, index) => <div className="developer-api-status" key={state}><span>{index + 1}</span>{state}</div>)}
        </div>
        <CodeFlow lines={['Message Created', '      ↓', 'Sent', '      ↓', 'Delivered', '      ↓', 'Read']} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Real-Time Applications" title="Build Real-Time WhatsApp Applications">
          Applications that need live communication can combine API requests, webhooks, and real-time technologies.
        </SectionHeading>
        <CodeFlow lines={['WhatsApp Event', '      ↓', 'Webhook', '      ↓', 'Backend', '      ↓', 'Socket / Real-Time Layer', '      ↓', 'Frontend']} />
        <p className="business-api-copy">This can allow a CRM or support dashboard to update message activity without requiring a full page refresh.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Reliability" title="WhatsApp API Error Handling">
          Production integrations need robust error handling. Log API errors without exposing sensitive credentials.
        </SectionHeading>
        <BulletList items={apiErrors} />
        <h3 className="business-api-subheading">Recommended Error Flow</h3>
        <CodeFlow lines={['API Request', '    ↓', 'Response', '    ↓', 'Success? ── Yes → Store Result', '    │', '    No', '    ↓', 'Log Error', '    ↓', 'Handle / Retry When Appropriate']} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Best Practices" title="WhatsApp API Integration Best Practices" />
        <CardGrid items={bestPractices} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Quality Assurance" title="WhatsApp API Testing">
          Test the complete communication flow before deploying your WhatsApp integration to production.
        </SectionHeading>
        <StepList items={testingSteps} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Documentation" title="WhatsApp API Documentation for Developers">
          Good API documentation is essential for development teams. Implementation documentation should clearly explain:
        </SectionHeading>
        <BulletList items={documentationTopics} />
        <p className="business-api-copy">HAWKTRION provides API-focused functionality so developers can connect applications to WhatsApp communication workflows.</p>
        <a href="https://docs-waba.hawktrion.com/" target="_blank" rel="noopener noreferrer" className="business-api-text-link">Explore the HAWKTRION API Documentation</a>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Automation" title="Build WhatsApp Automation With APIs">
          Combine application events with WhatsApp APIs to create automated workflows.
        </SectionHeading>
        <div className="saas-api-event-examples">
          <article><span>NEW CUSTOMER</span><strong>Customer created → CRM event → WhatsApp API → welcome message</strong></article>
          <article><span>APPOINTMENT</span><strong>Appointment created → schedule → WhatsApp template → notification</strong></article>
          <article><span>ORDER</span><strong>Order confirmed → backend event → WhatsApp API → order notification</strong></article>
        </div>
        <p className="business-api-copy">This makes WhatsApp a programmable communication channel inside your application.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="SaaS Development" title="WhatsApp API for SaaS Developers">
          A SaaS application can provide customers with WhatsApp account management, messaging, templates, campaigns, notifications, CRM integration, automation, and message tracking.
        </SectionHeading>
        <p className="business-api-copy">HAWKTRION can serve as the WhatsApp communication layer while your SaaS application manages its users, subscriptions, business logic, and interface.</p>
        <Link to="/whatsapp-api-for-saas/" className="business-api-text-link">Learn more about WhatsApp API for SaaS</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Startups" title="WhatsApp API for Startups">
          Add WhatsApp communication to your product without building every messaging component from scratch. Start with the functionality customers need and expand the integration as the product grows.
        </SectionHeading>
        <BulletList items={startupUseCases} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Scalable Systems" title="WhatsApp API Architecture">
          Separate application logic from external communication services to make an implementation easier to maintain and extend.
        </SectionHeading>
        <CodeFlow lines={[
          '                    Your Application',
          '                          │',
          '             ┌────────────┴────────────┐',
          '             │                         │',
          '        Frontend                    Backend',
          '                                       │',
          '                              Business Logic',
          '                                       │',
          '                              WhatsApp Service',
          '                                       │',
          '                                HAWKTRION API',
          '                                       │',
          '                              WhatsApp Platform',
          '                                       │',
          '                                   Customer',
        ]} />
        <BulletList items={architectureConsiderations} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Why HAWKTRION" title="Why Developers Choose HAWKTRION" />
        <CardGrid items={developerBenefits} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Integration Map" title="Supported Development Use Cases" />
        <div className="cloud-api-table-wrap">
          <table className="cloud-api-table">
            <caption>Developer requirements and HAWKTRION use cases</caption>
            <thead><tr><th scope="col">Developer Requirement</th><th scope="col">HAWKTRION Use Case</th></tr></thead>
            <tbody>{developerRequirements.map(([requirement, useCase]) => <tr key={requirement}><th scope="row">{requirement}</th><td>{useCase}</td></tr>)}</tbody>
          </table>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="End-to-End Flow" title="From API Request to Customer Message" />
        <StepList items={requestSteps} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Launch Checklist" title="Developer Checklist for WhatsApp API Integration" />
        <Checklist items={developerChecklist} />
      </div>
    </section>

    <section className="business-api-section business-api-faq" id="developer-api-faq">
      <div className="container">
        <SectionHeading eyebrow="Answers" title="WhatsApp API for Developers FAQs" />
        <div className="business-api-faq-list">
          {faqs.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}
        </div>
      </div>
    </section>

    <section className="business-api-section business-api-final-cta">
      <div className="container">
        <div className="business-api-cta-panel">
          <SectionHeading eyebrow="Build Your Integration" title="Start Building With WhatsApp API">
            Your application already has the business logic. Add WhatsApp communication with HAWKTRION APIs, templates, media, webhooks, automation, campaigns, and CRM workflows.
          </SectionHeading>
          <div className="business-api-actions">
            <Link to="/signup" className="btn btn-p">Get Started</Link>
            <Link to="/whatsapp-api-documentation/" className="btn btn-s">View API Documentation</Link>
            <a href="mailto:support@hawktrion.com?subject=WhatsApp%20API%20Developer%20Integration" className="btn btn-s">Contact HAWKTRION</a>
          </div>
        </div>
        
      </div>
    </section>
  </div>
);

export default WhatsappApiForDevelopersContent;